import {useCallback, useMemo} from 'react'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {roleApi} from '~/src/services'
import {agencyApi, useLazyGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {applicantsApi} from '~/src/store/apis/applicants-api'
import {companyApi} from '~/src/store/apis/company-api'
import {jobOrderApi} from '~/src/store/apis/job-order-api'
import {serviceOrderApi} from '~/src/store/apis/service-order-api'
import {userApi} from '~/src/store/apis/user-api'
import {selectAgencyState} from '~/src/store/slices/agency-slice'
import {clearApplicantsState} from '~/src/store/slices/applicants-slice'
import {setBackendRolePermissions, updateLoggedInAgencyId} from '~/src/store/slices/root-slice'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import {resolveEffectiveModulePermissions} from '~/src/utils/effectiveModulePermissions'
import {reportCatchError} from '~/src/utils/errorReporter'
import type {BackendRolePermissions} from '~/src/utils/permissions'
import type {ModulePermission} from '~/src/utils/routeUtils'

/** A branch the header's switcher offers. */
export interface SwitchableAgency {
  id: string
  name: string
}

/** The role tree a multi-agency user switched into, as stored in `backendRolePermissions`. */
export interface SwitchedAgencyPermissions {
  roleName: string
  /** Absent for a role with no tree (getRouteForRole then falls back to the role's default). */
  modulePermissions: ModulePermission[] | undefined
  backendRolePermissions: BackendRolePermissions
}

export interface SwitchAgencyOptions {
  /** Runs once the branch is selected, before its permissions are read (the switcher closes then). */
  onSelected?: () => void
}

export interface SwitchAgencyResult {
  /**
   * Absent when the tree was not recomputed: a user without `agencyAssignments` (a general admin
   * keeps one tree), or a failure to read it (already reported, with an error toast).
   */
  permissions?: SwitchedAgencyPermissions
}

/**
 * The branches the header's switcher (SelectAgencyMenu) offers: a multi-agency user's
 * `agencyAssignments`, else the general admin's `agencyNamesAndIds`.
 */
export function useSwitchableAgencies(): SwitchableAgency[] {
  const {agencyNamesAndIds} = useAppSelector(selectAgencyState)
  const {currentUser} = useCurrentUser()
  return useMemo(() => {
    if (currentUser?.agencyAssignments && currentUser.agencyAssignments.length > 0) {
      return currentUser.agencyAssignments.map((assignment) => ({
        id: assignment.agencyId,
        name: assignment.agencyName || assignment.agencyId, // Fallback to ID if name not available
      }))
    }
    return agencyNamesAndIds
  }, [currentUser?.agencyAssignments, agencyNamesAndIds])
}

/**
 * Selects another of the user's branches, as the header's switcher does: reads the agency, makes it
 * `loggedInAgencyId`, recomputes a multi-agency user's permissions for it, and resets the caches
 * that hold the previous branch's data. Navigating afterwards is the caller's: the switcher goes to
 * the role's first page, a job order invitation notification to Pending Orders (STFFLG-4184).
 *
 * Rejects when the agency cannot be read (nothing has changed then). A failure to recompute the
 * permissions is reported and toasted here, and the switch still completes, as it always has.
 */
export function useSwitchAgency(): (
  agencyId: string,
  options?: SwitchAgencyOptions,
) => Promise<SwitchAgencyResult> {
  const dispatch = useAppDispatch()
  const [getAgency] = useLazyGetOneAgencyQuery()
  const {currentUser} = useCurrentUser()
  const {showError} = useGlobalSnackbar()

  // Recomputes the permissions for a multi-agency user's branch.
  const updateAgencyPermissions = useCallback(
    async (agencyId: string): Promise<SwitchedAgencyPermissions | undefined> => {
      if (!currentUser?.agencyAssignments || currentUser.agencyAssignments.length === 0) return

      const permissionsResponse = await roleApi.getPermissionsForRoles(
        currentUser.roles as string[],
      )

      const primaryRole = permissionsResponse.roles[0]
      if (!primaryRole) return

      // Check if this agency has custom permissions
      const selectedAssignment = currentUser.agencyAssignments.find((a) => a.agencyId === agencyId)

      // Permission resolution (resolveEffectiveModulePermissions, STFFLG-4243):
      // 1. Agency overrides are authoritative — STFFLG-2281 allows escalation
      // 2. User overrides are restrictive (AND) — per-user restrictions only tighten
      // A role with no tree keeps that (empty or absent) tree: the agency's or the user's own tree
      // never stands in for it.
      const rolePermissions = primaryRole.modulePermissions as ModulePermission[] | undefined
      const modulePermissions =
        resolveEffectiveModulePermissions({
          roleTree: rolePermissions,
          agencyTree: selectedAssignment?.modulePermissions as ModulePermission[] | undefined,
          userTree: currentUser.modulePermissions as ModulePermission[] | undefined,
        }) ?? rolePermissions

      const backendRolePermissions: BackendRolePermissions = {
        _id: primaryRole.id,
        id: primaryRole.id,
        name: primaryRole.name,
        accessList: primaryRole.accessList,
        modulePermissions: modulePermissions,
        isPlatform: primaryRole.category === 'Platform',
        // Store category for layout variant determination (custom roles need this)
        category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
        application: 'msp',
        createdDate: primaryRole.createdDate?.toString() ?? '',
        modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
        versionKey: Number(primaryRole.versionKey) || 0,
      }
      dispatch(setBackendRolePermissions(backendRolePermissions))

      return {roleName: primaryRole.name, modulePermissions, backendRolePermissions}
    },
    [currentUser, dispatch],
  )

  return useCallback(
    async (agencyId: string, options?: SwitchAgencyOptions): Promise<SwitchAgencyResult> => {
      const agency = await getAgency(agencyId).unwrap()

      dispatch(updateLoggedInAgencyId(agency.id))
      options?.onSelected?.()

      // Update permissions for multi-agency users when switching agencies
      let permissions: SwitchedAgencyPermissions | undefined
      try {
        permissions = await updateAgencyPermissions(agencyId)
      } catch (permError) {
        reportCatchError('Failed to update permissions for agency:', permError)
        showError('Failed to update permissions. Please refresh the page.')
      }

      // Reset all API caches to ensure fresh data for the new agency context
      dispatch(companyApi.util.resetApiState())
      dispatch(serviceOrderApi.util.resetApiState())
      dispatch(jobOrderApi.util.resetApiState())
      // Clear applicants slice state first (prevents stale candidate data showing)
      dispatch(clearApplicantsState())
      dispatch(applicantsApi.util.resetApiState())
      dispatch(userApi.util.resetApiState())
      dispatch(agencyApi.util.resetApiState())

      return permissions ? {permissions} : {}
    },
    [dispatch, getAgency, showError, updateAgencyPermissions],
  )
}
